import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

const SmartServiceSelector = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  const questions = [
    {
      id: 'concern',
      question: 'What\'s your primary concern right now?',
      options: [
        { value: 'books', label: 'My books are behind or messy', service: 'accounting' },
        { value: 'taxes', label: 'I\'m worried about taxes or tax bills', service: 'tax' },
        { value: 'irs', label: 'I have IRS notices or back taxes', service: 'irs' },
        { value: 'entity', label: 'I\'m starting or restructuring a business', service: 'entity' },
        { value: 'value', label: 'I want to know what my business is worth', service: 'valuation' },
        { value: 'decision', label: 'I\'m facing a big decision', service: 'consulting' },
        { value: 'learn', label: 'I want to learn on my own first', service: 'tools' }
      ]
    },
    {
      id: 'type',
      question: 'Are you a business owner or individual?',
      options: [
        { value: 'business', label: 'Business owner' },
        { value: 'individual', label: 'Individual/Family' },
        { value: 'both', label: 'Both' }
      ]
    },
    {
      id: 'timeline',
      question: 'What\'s your timeline?',
      options: [
        { value: 'urgent', label: 'Need help NOW (urgent)' },
        { value: 'soon', label: 'Within 1-3 months' },
        { value: 'planning', label: 'Planning ahead (6+ months)' }
      ]
    },
    {
      id: 'current',
      question: 'Do you currently have a CPA or bookkeeper?',
      options: [
        { value: 'none', label: 'No one helping me' },
        { value: 'bookkeeper', label: 'Have bookkeeper, no CPA' },
        { value: 'cpa', label: 'Have CPA, looking to switch' },
        { value: 'focused', label: 'Just need help with one thing' }
      ]
    }
  ];

  const serviceRecommendations = {
    accounting: {
      name: 'Accounting & Payroll',
      tagline: 'Clarity without chaos.',
      description: 'Get your books clean and current so everything else becomes clearer.',
      link: '/services/accounting-payroll'
    },
    tax: {
      name: 'Tax Services',
      tagline: 'Compliance handled. Strategy built in.',
      description: 'Comprehensive tax preparation and year-round planning.',
      link: '/services/tax'
    },
    irs: {
      name: 'IRS Representation & Relief',
      tagline: 'Calm, clarity, and representation.',
      description: 'Professional help when facing IRS or state tax notices.',
      link: '/services/irs-relief'
    },
    entity: {
      name: 'Entity Creation & Restructuring',
      tagline: 'Start right. Structure for savings.',
      description: 'Choose and implement the right entity design for your business.',
      link: '/services/entity'
    },
    valuation: {
      name: 'Business Valuation & Growth',
      tagline: 'Know your value. Then make it grow.',
      description: 'Understand your business value and how to increase it.',
      link: '/services/valuation'
    },
    consulting: {
      name: 'Strategic Consulting',
      tagline: 'When the numbers need to drive the decision.',
      description: 'Get financially grounded perspective on big questions.',
      link: '/services/consulting'
    },
    tools: {
      name: 'Financial Tools & Resources',
      tagline: 'Self-serve wins without waiting.',
      description: 'Access curated tools and resources on your own time.',
      link: '/services/tools'
    }
  };

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const getRecommendations = () => {
    const recommendations = [];
    
    // Primary recommendation based on concern
    const concernAnswer = (questions[0].options || []).find(opt => opt.value === answers.concern);
    if (concernAnswer && concernAnswer.service) {
      recommendations.push({ ...serviceRecommendations[concernAnswer.service], priority: 'primary' });
    }

    // Add secondary recommendations based on context
    if (answers.type === 'business' && answers.current === 'none') {
      // No CPA and business owner -> suggest accounting first
      if (!recommendations.find(r => r.name === 'Accounting & Payroll')) {
        recommendations.push({ ...serviceRecommendations.accounting, priority: 'secondary' });
      }
    }

    if (answers.timeline === 'urgent' && answers.concern !== 'irs') {
      // Urgent timeline -> might need IRS relief or consulting
      if (!recommendations.find(r => r.name === 'IRS Representation & Relief')) {
        recommendations.push({ ...serviceRecommendations.irs, priority: 'secondary' });
      }
    }

    if (answers.type === 'business' && answers.concern === 'taxes') {
      // Business owner worried about taxes -> might also need accounting
      if (!recommendations.find(r => r.name === 'Accounting & Payroll')) {
        recommendations.push({ ...serviceRecommendations.accounting, priority: 'secondary' });
      }
    }

    return recommendations.slice(0, 3); // Max 3 recommendations
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
  };

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const canProceed = answers[currentQ?.id];

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Smart Service Selector | KGOB CPAs</title>
        <meta name="description" content="Find the right KGOB service for your needs in less than a minute with our Smart Service Selector quiz." />
        <link rel="canonical" href="https://kgob.com/services/selector" />
      </Helmet>
      
      <Header />
      
      <main className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          {!showResults ? (
            <>
              {/* Quiz Header */}
              <div className="text-center mb-12">
                <h1 className="font-['Playfair_Display'] text-4xl font-bold text-[#0B2240] mb-4">
                  Smart Service Selector
                </h1>
                <p className="text-lg text-[#4C5A70]">
                  Answer 4 quick questions and we'll recommend the best services for your situation.
                </p>
              </div>

              {/* Progress Bar */}
              <div className="mb-8">
                <div className="h-2 bg-[#E1DFD7] rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-[linear-gradient(90deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)]"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <p className="text-sm text-[#4C5A70] mt-2 text-center">
                  Question {currentQuestion + 1} of {questions.length}
                </p>
              </div>

              {/* Question Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentQuestion}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="p-8 border-2 border-[#E1DFD7]">
                    <h2 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-6">
                      {currentQ.question}
                    </h2>
                    
                    <RadioGroup 
                      value={answers[currentQ.id]} 
                      onValueChange={(value) => handleAnswer(currentQ.id, value)}
                    >
                      <div className="space-y-3">
                        {(currentQ.options || []).map((option) => (
                          <div 
                            key={option.value} 
                            className="flex items-center space-x-3 p-4 rounded-lg border-2 border-[#E1DFD7] hover:border-[#D4AF37] hover:bg-[#F7F6F2] transition-all cursor-pointer"
                          >
                            <RadioGroupItem value={option.value} id={option.value} />
                            <Label 
                              htmlFor={option.value} 
                              className="flex-1 cursor-pointer text-[#4C5A70]"
                            >
                              {option.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </RadioGroup>
                  </Card>
                </motion.div>
              </AnimatePresence>

              {/* Navigation Buttons */}
              <div className="flex gap-4 mt-8">
                {currentQuestion > 0 && (
                  <Button data-testid="smart-service-selector-button-7" 
                    onClick={handleBack}
                    variant="outline"
                    className="h-12 px-6 border-2 border-[#0B2240] text-[#0B2240]"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                )}
                
                <Button data-testid="smart-service-selector-button-6" 
                  onClick={handleNext}
                  disabled={!canProceed}
                  className="flex-1 h-12 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {currentQuestion === questions.length - 1 ? 'See My Recommendations' : 'Next Question'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Results */}
              <div className="text-center mb-12">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] mb-6">
                    <CheckCircle className="w-10 h-10 text-[#0B2240]" />
                  </div>
                  <h1 className="font-['Playfair_Display'] text-4xl font-bold text-[#0B2240] mb-4">
                    Your Recommended Services
                  </h1>
                  <p className="text-lg text-[#4C5A70]">
                    Based on your answers, here are the services we recommend for your situation:
                  </p>
                </motion.div>
              </div>

              {/* Recommendations */}
              <div className="space-y-6 mb-10">
                {getRecommendations().map((rec, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.2 }}
                  >
                    <Card className={`p-8 border-2 ${
                      rec.priority === 'primary' 
                        ? 'border-[#D4AF37] bg-gradient-to-br from-white to-[#F7F6F2]' 
                        : 'border-[#E1DFD7]'
                    }`}>
                      {rec.priority === 'primary' && (
                        <div className="inline-block px-3 py-1 bg-[#D4AF37] text-[#0B2240] text-xs font-bold rounded-full mb-4">
                          PRIMARY RECOMMENDATION
                        </div>
                      )}
                      <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-2">
                        {rec.name}
                      </h3>
                      <p className="text-sm text-[#D4AF37] italic mb-4">{rec.tagline}</p>
                      <p className="text-[#4C5A70] mb-6">{rec.description}</p>
                      
                      <div className="flex gap-3">
                        <Button data-testid="smart-service-selector-button-5" asChild className="bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                          <Link data-testid="smart-service-selector-link-4" to={rec.link}>Learn More About {rec.name}</Link>
                        </Button>
                        <Button data-testid="smart-service-selector-button-4" asChild variant="outline" className="border-2 border-[#0B2240] text-[#0B2240]">
                          <Link data-testid="smart-service-selector-link-3" to="/book-consultation?source=service-selector">Book Consultation</Link>
                        </Button>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button data-testid="smart-service-selector-button-3" 
                  onClick={handleRestart}
                  variant="outline"
                  className="h-12 px-8 border-2 border-[#0B2240] text-[#0B2240]"
                >
                  Start Over
                </Button>
                <Button data-testid="smart-service-selector-button-2" asChild className="h-12 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                  <Link data-testid="smart-service-selector-link-2" to="/services">View All Services</Link>
                </Button>
                <Button data-testid="smart-service-selector-button-1" asChild variant="outline" className="h-12 px-8 border-2 border-[#0B2240] text-[#0B2240]">
                  <Link data-testid="smart-service-selector-link-1" to="/book-consultation?source=service-selector">Book Consultation</Link>
                </Button>
              </div>

              {/* Additional Context */}
              <Card className="mt-10 p-6 border-2 border-[#E1DFD7] bg-[#F7F6F2]">
                <p className="text-sm text-[#4C5A70] text-center">
                  Not sure which option is right? No problem. During your consultation, we'll help you determine the best path forward based on your specific situation.
                </p>
              </Card>
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default SmartServiceSelector;